import React from 'react';

import EmailCommunicationPopover from '~/components/Contacts/ContactCommunications/Email/EmailCommunicationPopover';
import SmsCommunicationPopover from '~/components/Contacts/ContactCommunications/SMS/SmsCommunicationPopover';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import { FeedbackSnackbar, useSnackbar } from '~/components/core/Molecules/Snackbar';
import Caption from '~/components/core/TextComponents/Caption';
import Text from '~/components/core/TextComponents/Text';
import { htmlToTextTemplateParser } from '~/components/GenericTemplates/utils/genericTemplatesUtils';
import FilesIcon from '~/components/icons/FilesIcon';
import type { ContactFullModel } from '~/server_shared/swagger/data-contracts';

export interface LinkShareTemplateData {
  title_email_html: string;
  body_email_html: string;
  sms_html?: string;
}

interface Props {
  claimId: number;
  title: string;
  href: string;
  contact: ContactFullModel;
  // The SMS and email actions are offered once it is given; the link is copyable before that.
  templateData?: LinkShareTemplateData;
  // Opens the SMS composer with one line per template paragraph; off by default.
  keepSmsParagraphs?: boolean;
}

const LinkShare: React.FC<Props> = ({ claimId, title, href, contact, templateData, keepSmsParagraphs = false }) => {
  const { snackbarState, showSnackbar, closeSnackbar } = useSnackbar();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(href);
      showSnackbar('Copied to clipboard', 'success');
    } catch (error) {
      showSnackbar('Failed to copy to clipboard', 'error');
    }
  };

  return (
    <div className="my-20 flex justify-between bg-slate-200 p-12">
      <div className="mr-12 overflow-hidden">
        <Caption>{title}</Caption>
        <Text
          colorVariant={Text.COLOR_VARIANTS.SECONDARY}
          variant={Text.VARIANTS.XS}
          weight={Text.WEIGHTS.REGULAR}
          className="truncate"
        >
          {href}
        </Text>
      </div>

      <div className="flex flex-row flex-nowrap items-center">
        {templateData && (
          <>
            <SmsCommunicationPopover
              contact={contact}
              useIconButton
              messageContent={htmlToTextTemplateParser(templateData.sms_html || '', keepSmsParagraphs)}
              claimId={claimId}
              ariaLabel="Send SMS"
            />
            <EmailCommunicationPopover
              contact={contact}
              useIconButton
              emailTitle={htmlToTextTemplateParser(templateData.title_email_html)}
              emailText={templateData.body_email_html}
              claimId={claimId}
              ariaLabel="Send email"
            />
          </>
        )}
        <IconButton onClick={handleCopy} className="p-12 text-slate-900 hover:text-teal-700" aria-label="Copy link">
          <FilesIcon iconColor="currentColor" />
        </IconButton>
      </div>
      <FeedbackSnackbar state={snackbarState} onClose={closeSnackbar} />
    </div>
  );
};

export default LinkShare;
